import { useQuery } from "@tanstack/react-query";
import {
  Link,
  Outlet,
  createFileRoute,
  useParams,
} from "@tanstack/react-router";
import { PlusCircle } from "lucide-react";
import {
  FpSidebar,
  FpSidebarContent,
  FpSidebarGroup,
  FpSidebarGroupContent,
  FpSidebarHeader,
  FpSidebarInset,
  FpSidebarMenu,
  FpSidebarMenuButton,
  FpSidebarMenuItem,
  FpSidebarProvider,
  FpSidebarRail,
  FpSidebarTrigger,
} from "../../components/ui/fp-sidebar";
import type { Agent } from "../../lib/hooks/use-agents";
import {
  listPavilionAgentsQueryOptions,
  useCreatePavilionAgent,
} from "../../lib/hooks/usePavilionAgents";

export const Route = createFileRoute("/(pavilion)/pavilion")({
  component: PavilionLayout,
  loader: async ({ context: { queryClient } }) => {
    // Prefetch agents list
    await queryClient.ensureQueryData(listPavilionAgentsQueryOptions());
  },
});

function PavilionLayout() {
  const params = useParams({ strict: false });
  const currentAgentName = params.agentName;

  // Fetch agent data using TanStack Query
  const {
    data: agentsResponse,
    isLoading,
    isError,
  } = useQuery(listPavilionAgentsQueryOptions());

  const agents = agentsResponse?.data?.agents || [];

  // Use the custom hook for creating agents
  const createAgentMutation = useCreatePavilionAgent();

  const handleCreateAgent = () => {
    createAgentMutation.mutate({
      systemPrompt: "You are a helpful AI assistant.",
      description: "",
      modelProvider: "anthropic",
      modelName: "claude-sonnet-4-20250514",
      tools: [],
    });
  };

  return (
    <div className="h-screen flex flex-col">
      <FpSidebarProvider defaultOpen={true}>
        <FpSidebar collapsible="icon" colorScheme="background">
          <FpSidebarHeader className="h-12">
            <div className="flex items-center gap-2">
              <FpSidebarTrigger />
              <h2 className="font-semibold group-data-[collapsible=icon]:hidden">
                Pavilion
              </h2>
            </div>
          </FpSidebarHeader>
          <FpSidebarContent>
            <FpSidebarGroup>
              <FpSidebarGroupContent>
                <FpSidebarMenu>
                  {/* New Agent Button as first item */}
                  <FpSidebarMenuItem>
                    <FpSidebarMenuButton
                      tooltip="Create new agent"
                      onClick={handleCreateAgent}
                      disabled={createAgentMutation.isPending}
                      className="flex items-center gap-2"
                    >
                      <div className="w-6 h-6 border-2 border-dashed flex items-center justify-center flex-shrink-0">
                        <PlusCircle className="h-4 w-4" />
                      </div>
                      <div className="text-left min-w-0 flex-1 group-data-[collapsible=icon]:hidden">
                        <div className="font-medium text-sm truncate">
                          {createAgentMutation.isPending
                            ? "Creating..."
                            : "New Agent"}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          Create a new AI agent
                        </div>
                      </div>
                    </FpSidebarMenuButton>
                  </FpSidebarMenuItem>

                  {isError ? (
                    <div className="p-4 text-center text-red-500">
                      Error loading agents
                    </div>
                  ) : isLoading ? (
                    <div className="p-4 text-center text-muted-foreground">
                      Loading agents...
                    </div>
                  ) : agents.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      No agents yet
                    </div>
                  ) : (
                    agents.map((agent: Agent) => (
                      <FpSidebarMenuItem key={agent.id}>
                        <FpSidebarMenuButton
                          asChild
                          tooltip={agent.name}
                          isActive={currentAgentName === agent.name}
                        >
                          <Link
                            to="/pavilion/$agentName"
                            params={{ agentName: agent.name }}
                            search={{ leftTab: "system", rightTab: "console" }}
                            className="flex items-center gap-2"
                            preload="viewport"
                          >
                            <div className="w-6 h-6 bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-medium">
                                {agent.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="text-left min-w-0 flex-1 group-data-[collapsible=icon]:hidden">
                              <div className="font-medium text-sm truncate">
                                {agent.name}
                              </div>
                              {agent.description && (
                                <div className="text-xs text-muted-foreground truncate">
                                  {agent.description}
                                </div>
                              )}
                            </div>
                          </Link>
                        </FpSidebarMenuButton>
                      </FpSidebarMenuItem>
                    ))
                  )}
                </FpSidebarMenu>
              </FpSidebarGroupContent>
            </FpSidebarGroup>
          </FpSidebarContent>
          <FpSidebarRail />
        </FpSidebar>
        <FpSidebarInset className="flex-1 flex flex-col min-h-0">
          <Outlet />
        </FpSidebarInset>
      </FpSidebarProvider>
    </div>
  );
}
